import { decouperAjouts } from '@/lib/ajouts-texte';
import type { Segment } from '@/lib/invariants';
import type { AjoutControle, ChangementTraduction, Invariant } from '@rosetta/shared';
import { MarqueAjout } from './marque-ajout';
import { MarqueChangement } from './marque-changement';
import { MarqueInvariant } from './marque-invariant';
import { MarquePassage } from './marque-passage';

interface TexteTraduitAjoutsProps {
  texte: string;
  invariants?: readonly Invariant[] | null | undefined;
  ajouts: readonly AjoutControle[];
  changements?: readonly ChangementTraduction[] | undefined;
  /** The passages of the "Bloquant" points of other indicators, marked where written as they are. */
  passages?: readonly string[] | undefined;
}

function Segments({ segments }: { segments: readonly Segment[] }) {
  // The position in the text is the key: the pieces are never reordered.
  let position = 0;
  return segments.map((segment) => {
    const cle = position;
    position += segment.texte.length;
    return segment.type === 'texte' ? (
      <span key={cle}>{segment.texte}</span>
    ) : (
      <MarqueInvariant key={cle} verrou={segment.verrou}>
        {segment.texte}
      </MarqueInvariant>
    );
  });
}

/**
 * The translated text of "Avant / après" and of "Texte traduit" with the additions of the control
 * marked (#23), the changes the translation declares (#28) and, in "Texte traduit", the passages
 * the control blocks on, and the invariants marked as before, inside them when they overlap. The
 * characters are the ones of the text.
 */
export function TexteTraduitAjouts({
  texte,
  invariants,
  ajouts,
  changements,
  passages,
}: TexteTraduitAjoutsProps) {
  let position = 0;
  return decouperAjouts(texte, ajouts, invariants, changements, passages).map((bloc) => {
    const cle = position;
    position += bloc.segments.reduce((total, segment) => total + segment.texte.length, 0);
    switch (bloc.type) {
      case 'texte':
        return <Segments key={cle} segments={bloc.segments} />;
      case 'ajout':
        return (
          <MarqueAjout key={cle} ajout={bloc.ajout}>
            <Segments segments={bloc.segments} />
          </MarqueAjout>
        );
      case 'passage':
        return (
          <MarquePassage key={cle}>
            <Segments segments={bloc.segments} />
          </MarquePassage>
        );
      case 'changement':
        return (
          <MarqueChangement key={cle} changement={bloc.changement}>
            <Segments segments={bloc.segments} />
          </MarqueChangement>
        );
    }
  });
}
